import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Grid } from '@material-ui/core'
import { useFormikContext } from 'formik'

import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import RadioButtonNew from 'presentation/shared/components/RadioButtonNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import * as S from './styles'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { toast } from 'react-toastify'
import { useServices } from 'presentation/hooks/use-services'
import { useStores } from 'presentation/hooks/use-stores'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { formatCpf } from 'common/utils/format-cpf'
import {
  cpfMask,
  numbersOnlyRegex,
  phoneMaskWithParenthesis
} from 'presentation/utils/masks'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { GenderNew, genderNew } from 'common/enum/gender'
import {
  MaritalStatus,
  maritalStatus
} from 'presentation/utils/default-marital-status'
import { RegistrationInfoFormValues } from 'presentation/contexts/surgical-order-v2/types'
import { validateCpf } from 'presentation/utils/validators/cpf-validator'
import { isPatientRN } from 'presentation/utils/validators/is-patient-rn'
import DatePickerNew from 'presentation/shared/components/DatePickerNew'
import moment from 'moment-timezone'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import { Patient } from 'domain/entities/patient-model'
import { runInAction } from 'mobx'
import SelectPatientResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/SelectPatientResponsiveModal'
import PatientConfirmationResponsiveModal from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderResponsiveModals/PatientConfirmationResponsiveModal'
import { COOPERATIVE_HOSPITALS } from 'presentation/contexts/surgical-order-v2/initial-state'

type PatientListSuggestion = {
  label: string
  value: Patient
}

const FormRegistrationInfo = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const store = useStores().surgicalOrderV2
    const hospitalService = useServices().hospital
    const patientService = useServices().patient
    const [searchBy, setSearchBy] = useState<'cpf' | 'name'>('cpf')
    const [searchName, setSearchName] = useState('')
    const [searchBirthday, setSearchBirthday] = useState('')
    const [searchedPatientNameList, setSearchedPatientNameList] = useState<
      PatientListSuggestion[]
    >([])
    const [foundPatients, setFoundPatients] = useState<Patient[]>([])
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
    const [isSelectPatientModalOpen, setIsSelectPatientModalOpen] =
      useState<boolean>(false)
    const [isPatientConfirmationModalOpen, setIsPatientConfirmationModalOpen] =
      useState<boolean>(false)
    const formik = useFormikContext<RegistrationInfoFormValues>()

    const insuranceOptions = (store.healthPlans ?? []).map((hi) => ({
      value: hi.health_insurance_id,
      label: hi.name
    }))

    const healthPlanOptions = (store.healthPlans ?? [])
      .filter(
        (hi) =>
          hi.health_insurance_id === Number(formik.values.healthInsuranceCode)
      )
      .flatMap((hi) =>
        (hi.plans ?? []).map((plan) => ({
          value: plan.plan_id,
          label: plan.name
        }))
      )

    const healthSubPlanOptions = store.healthSubPlans.map((sub) => ({
      value: sub.sub_plan_id,
      label: sub.name
    }))

    const handleChangeHospital = (id: string) => {
      store.surgeryCenters = []
      const hospital = store.hospitals.find((h) => h.hospital_id === Number(id))
      formik.setFieldValue('hospitalId', Number(id))
      formik.setFieldValue('hospitalName', hospital?.friendly_name)
      formik.setFieldValue('surgeryCenterId', '')
      formik.setFieldValue('surgeryCenterName', '')
      formik.setFieldValue('cooperative', '')
    }

    const handleChangeSurgicalUnit = (id: string) => {
      const surgeryCenter = store.surgeryCenters.find(
        (center) => Number(center.surgery_center_id) === Number(id)
      )
      formik.setFieldValue('surgeryCenterId', id)
      formik.setFieldValue('surgeryCenterName', surgeryCenter?.name)
    }

    const handleCpfChange = async (newValue: string) => {
      const cleanCpf = newValue.replace(/\D/g, '')
      if (cleanCpf.length !== 11 || !validateCpf(cleanCpf)) {
        return
      }
      formik.setFieldValue('cpf', formatCpf(newValue))
      try {
        await store.loadPatientByCpf(newValue, patientService)
      } catch (error: any) {
        toast.error(error?.message ?? 'Ocorreu um erro ao buscar o paciente')
      }
    }

    const handleCpfTutorChange = async (newValue: string) => {
      const cleanCpf = newValue.replace(/\D/g, '')
      if (cleanCpf.length !== 11 || !validateCpf(cleanCpf)) {
        return
      }
      formik.setFieldValue('tutor.cpf', formatCpf(newValue))
      try {
        await store.loadTutorByCpf(newValue, patientService)
        // Sincroniza dados do tutor
        formik.setFieldValue('tutor.name', store.patient.tutor?.name || '')
        formik.setFieldValue('tutor.phone', store.patient.tutor?.phone || '')
        formik.setFieldValue('tutor.email', store.patient.tutor?.email || '')
        // Sincroniza dados de convênio (carregados via loadPatientHealthInsurance dentro de loadTutorByCpf)
        formik.setFieldValue(
          'healthInsuranceCode',
          store.healthInsurance.healthInsuranceCode || 0
        )
        formik.setFieldValue(
          'healthInsuranceName',
          store.healthInsurance.healthInsuranceName || ''
        )
        formik.setFieldValue(
          'healthPlanCode',
          store.healthInsurance.healthPlanCode || 0
        )
        formik.setFieldValue(
          'healthPlanName',
          store.healthInsurance.healthPlanName || ''
        )
        formik.setFieldValue(
          'healthSubplanCode',
          store.healthInsurance.healthSubplanCode || 0
        )
        formik.setFieldValue(
          'healthSubplanName',
          store.healthInsurance.healthSubplanName || ''
        )
        formik.setFieldValue(
          'healthCard',
          store.healthInsurance.healthCard || ''
        )
        formik.setFieldValue('validThru', store.healthInsurance.validThru || '')
      } catch (error: any) {
        toast.error(error?.message ?? 'Ocorreu um erro ao buscar o tutor')
      }
    }

    const handleClearPatient = () => {
      store.resetPatientSearch()
      formik.setValues((prev) => ({
        ...prev,
        cpf: '',
        name: '',
        birthDate: '',
        gender: '' as GenderNew,
        maritalStatus: '' as MaritalStatus,
        phone: '',
        email: '',
        healthInsuranceName: '',
        healthInsuranceCode: 0,
        healthCard: '',
        validThru: '',
        healthPlanName: '',
        healthPlanCode: 0,
        healthSubplanName: '',
        healthSubplanCode: 0,
        internation: undefined,
        tutor: {
          cpf: '',
          name: '',
          phone: '',
          email: ''
        }
      }))
      clearSelection()
    }

    const handleClearTutor = () => {
      store.resetTutorSearch()
      formik.setValues((prev) => ({
        ...prev,
        healthInsuranceName: '',
        healthInsuranceCode: 0,
        healthCard: '',
        validThru: '',
        healthPlanName: '',
        healthPlanCode: 0,
        healthSubplanName: '',
        healthSubplanCode: 0,
        tutor: {
          cpf: '',
          name: '',
          phone: '',
          email: ''
        }
      }))
    }

    const handleChangeHealthInsurance = (value: string) => {
      const selected = insuranceOptions.find(
        (opt) => String(opt.value) === value
      )
      store.healthSubPlans = []
      formik.setValues((prev) => ({
        ...prev,
        healthInsuranceCode: selected?.value ?? 0,
        healthInsuranceName: selected?.label ?? '',
        healthPlanCode: 0,
        healthPlanName: '',
        healthSubplanCode: 0,
        healthSubplanName: '',
        healthCard: '',
        validThru: ''
      }))
    }

    const handleChangeHealthPlan = async (value: string) => {
      const selected = healthPlanOptions.find(
        (opt) => String(opt.value) === value
      )
      formik.setValues((prev) => ({
        ...prev,
        healthPlanCode: selected?.value ?? 0,
        healthPlanName: selected?.label ?? '',
        healthSubplanCode: 0,
        healthSubplanName: '',
        healthCard: '',
        validThru: ''
      }))

      if (selected?.value && formik.values.healthInsuranceCode) {
        try {
          await store.loadSubPlans(
            Number(formik.values.healthInsuranceCode),
            selected.value,
            patientService
          )
        } catch {
          toast.error('Ocorreu um erro ao buscar os subplanos')
        }
      } else {
        store.healthSubPlans = []
      }
    }

    const handleChangeHealthSubplan = (value: string) => {
      const selected = healthSubPlanOptions.find(
        (opt) => String(opt.value) === value
      )
      formik.setValues((prev) => ({
        ...prev,
        healthSubplanCode: selected?.value ?? 0,
        healthSubplanName: selected?.label ?? '',
        healthCard: '',
        validThru: ''
      }))
    }

    const getPatients = async (patientName: string) => {
      try {
        if (patientName) {
          const searchedPatients = await patientService.getPatientByCpfOrName({
            name: patientName
          })

          const suggestionPatients = searchedPatients.map((patient) => ({
            label: patient?.name,
            value: patient
          }))

          setSearchedPatientNameList(suggestionPatients)
        } else {
          setSearchedPatientNameList([])
        }
      } catch (error: any) {
        console.error(error?.message)
        toast.error('Ocorreu um erro ao buscar pacientes')
        setSearchedPatientNameList([])
      }
    }

    const handleSearchByNameAndBirthday = async () => {
      if (!searchName || !searchBirthday) return

      setIsLoading(true)

      try {
        // TODO (Testar removendo o birthday do param para retornar mais de 1 patient):
        const response = await patientService.getPatientByCpfOrName({
          name: searchName,
          birthday: searchBirthday
        })

        if (response && response.length === 1) {
          // TODO:
          // Remover essa chamada quando o back implementar
          // o retorno do Tutor na busca por name + birthday
          if (
            response[0]?.birthday &&
            isPatientRN(moment(response[0]?.birthday).utc().toDate())
          ) {
            const responseRN = await patientService.getPatientByCpfOrName({
              patient_id: String(response[0]?.patient_id)
            })
            setSelectedPatient(responseRN[0])
            setIsSelectPatientModalOpen(true)
          } else {
            setSelectedPatient(response[0])
            setIsSelectPatientModalOpen(true)
          }
        } else if (response && response.length > 1) {
          setFoundPatients(response)
          setIsPatientConfirmationModalOpen(true)
        } else {
          runInAction(() => {
            store.patientSearched = true
            store.patientFound = false
            store.resetPatient()
            store.updatePatient({
              isNewPatient: true,
              name: searchName,
              birthDate: searchBirthday
            })
            store.resetHealthInsurance()
          })
          toast.warning(
            'A busca não retornou resultados. Cadastre um novo Paciente.'
          )
        }
      } catch (error: any) {
        toast.error(error?.message ?? 'Ocorreu um erro ao buscar o paciente')
        handleClearPatient()
      } finally {
        setIsLoading(false)
      }
    }

    const selectSearchedPatientName = (
      listSuggestion: PatientListSuggestion
    ) => {
      setSearchName(listSuggestion.label)
    }

    const clearSelection = () => {
      setSearchName('')
      setSearchBirthday('')
      setSearchedPatientNameList([])
      setFoundPatients([])
      setSelectedPatient(null)
    }

    const handleConfirmPatient = async () => {
      if (!selectedPatient) return
      setIsSelectPatientModalOpen(false)
      clearSelection()

      try {
        await store.loadPatientById(selectedPatient.patient_id, patientService)
      } catch (error) {
        console.error(error)
        toast.error('Ocorreu um erro ao carregar os dados do paciente')
      }
    }

    const handleCheckCPF = (cpf: string) => {
      const patientFound = foundPatients.find(
        (patient) => patient?.user?.cpf === cpf
      )

      if (patientFound) {
        setSelectedPatient(patientFound)
        setIsPatientConfirmationModalOpen(false)
        setIsSelectPatientModalOpen(true)
      } else {
        handleResetSearch()
        toast.warning(
          'O CPF informado não foi encontrado. Cadastre um novo paciente ou tente novamente.'
        )
      }
    }

    const handleResetSearch = () => {
      clearSelection()
      setIsSelectPatientModalOpen(false)
      setIsPatientConfirmationModalOpen(false)
    }

    // Carrega centros cirúrgicos quando o hospital muda
    // Pula na montagem inicial se já há centros no store (carregados pelo init do pedido existente)
    useEffect(() => {
      if (!formik.values.hospitalId) return
      if (store.surgeryCenters.length > 0) return

      const load = async () => {
        try {
          await store.loadSurgeryCenters(
            formik.values.hospitalId,
            hospitalService
          )
        } catch {
          toast.error('Ocorreu um erro ao buscar os centros cirúrgicos')
        }
      }
      load()
    }, [formik.values.hospitalId])

    // Sincroniza dados do paciente no Formik após o store terminar de carregar
    // Reage quando isLoadingPatient volta a false e patientSearched é true
    useEffect(() => {
      if (store.isLoadingPatient || !store.patientSearched) return

      const storeValues = store.stepRegistrationInfoFormValues

      formik.setValues((prev) => ({
        ...storeValues,
        hospitalId: prev.hospitalId,
        hospitalName: prev.hospitalName,
        surgeryCenterId: prev.surgeryCenterId,
        surgeryCenterName: prev.surgeryCenterName,
        cooperative: prev.cooperative
      }))
    }, [store.isLoadingPatient, store.patientSearched])

    // Sincroniza todos os loadings do store com o HOC
    useEffect(() => {
      setIsLoading(store.isLoading)
    }, [store.isLoading])

    return (
      <S.FormWrapper onSubmit={formik.handleSubmit}>
        <S.Section>
          <HeadingNew as="h3" color="textSecondary" size="medium">
            Local da cirurgia
          </HeadingNew>
          <SupportTextNew color="textPrimary" fontFamily="secondary">
            Onde o procedimento será realizado?
          </SupportTextNew>

          <div style={{ marginTop: '16px' }}>
            <Grid container spacing={2}>
              <S.StyledGridItem item xs={12} md={6}>
                <SelectFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  requiredColor="textSecondary"
                  label="Unidade"
                  name="hospitalId"
                  placeholder="Selecione unidade"
                  value={formik.values.hospitalId}
                  onInputChange={handleChangeHospital}
                  error={formik.touched.hospitalId && formik.errors.hospitalId}
                  required
                  items={store.hospitals.map(
                    ({ name, friendly_name, hospital_id }) => ({
                      value: hospital_id,
                      label: friendly_name ?? name
                    })
                  )}
                  height="42px"
                />
              </S.StyledGridItem>
              <S.StyledGridItem item xs={12} md={6}>
                <SelectFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  requiredColor="textSecondary"
                  label="Unidade cirúrgica"
                  name="surgeryCenterId"
                  placeholder="Selecione unidade cirúrgica"
                  value={formik.values.surgeryCenterId}
                  onInputChange={handleChangeSurgicalUnit}
                  disabled={!formik.values.hospitalId}
                  error={
                    formik.touched.surgeryCenterId &&
                    formik.errors.surgeryCenterId
                  }
                  required
                  items={store.surgeryCenters.map((center) => ({
                    value: center.surgery_center_id ?? '',
                    label: center.name ?? center.description ?? ''
                  }))}
                  height="42px"
                />
              </S.StyledGridItem>
            </Grid>
          </div>
        </S.Section>

        <S.Section>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <HeadingNew as="h3" color="textSecondary" size="medium">
              Dados do paciente
            </HeadingNew>
            {!store.surgicalOrder.orderWasLoadedInContext &&
              store.patientSearched && (
                <S.ClearButton size="small" onClick={handleClearPatient}>
                  Limpar
                </S.ClearButton>
              )}
          </div>

          {/* Campos de busca */}
          {!store.surgicalOrder.orderWasLoadedInContext &&
            !store.patientSearched && (
              <>
                <SupportTextNew color="textPrimary" fontFamily="secondary">
                  De que forma você prefere iniciar sua busca?
                </SupportTextNew>
                <S.RadioGroup style={{ margin: '8px 0' }}>
                  <RadioButtonNew
                    label="CPF"
                    labelFor="search_by_cpf"
                    labelColor="textPrimary"
                    showBackground={false}
                    name="searchBy"
                    checked={searchBy === 'cpf'}
                    onCheck={() => {
                      handleClearPatient()
                      setSearchBy('cpf')
                    }}
                  />
                  <RadioButtonNew
                    label="Nome + Data de Nascimento"
                    labelFor="search_by_name"
                    labelColor="textPrimary"
                    showBackground={false}
                    name="searchBy"
                    checked={searchBy === 'name'}
                    onCheck={() => {
                      handleClearPatient()
                      setSearchBy('name')
                    }}
                  />
                </S.RadioGroup>
              </>
            )}

          {/* Dados do paciente RN */}
          {store.patientSearched &&
            isPatientRN(new Date(formik.values.birthDate)) && (
              <>
                <div style={{ marginTop: '16px' }}>
                  <Grid container spacing={2}>
                    <S.StyledGridItem item xs={12}>
                      <TextFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Nome"
                        name="name"
                        placeholder="Digite nome"
                        value={formik.values.name}
                        onInputChange={(value) =>
                          formik.handleChange('name')(value)
                        }
                        onBlur={formik.handleBlur('name')}
                        error={formik.touched.name && formik.errors.name}
                        disabled={store.surgicalOrder.orderWasLoadedInContext}
                        required
                        height="40px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <DatePickerNew
                        labelOverlapping
                        label="Data de nasc. (Recém-nascido)"
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        placeholder="dd/mm/aaaa"
                        name="birthDate"
                        mainValue={formik.values.birthDate}
                        onMainChange={(date) =>
                          formik.setFieldValue('birthDate', date)
                        }
                        maxDate={new Date()}
                        disabled={store.surgicalOrder.orderWasLoadedInContext}
                        required
                        requiredColor="textSecondary"
                        closeOnChange
                        height="40px"
                        error={
                          formik.touched.birthDate && formik.errors.birthDate
                        }
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <SelectFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Sexo (Recém-nascido)"
                        name="gender"
                        placeholder="Selecione sexo"
                        value={formik.values.gender}
                        onInputChange={(value) =>
                          formik.setFieldValue('gender', value)
                        }
                        error={formik.touched.gender && formik.errors.gender}
                        disabled={store.surgicalOrder.orderWasLoadedInContext}
                        required
                        items={genderNew}
                        height="42px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <TextFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        // requiredColor="textSecondary"
                        label="CPF (Recém-nascido)"
                        name="cpf"
                        placeholder="Digite CPF"
                        value={formik.values.cpf}
                        // onInputChange={handleCpfChange}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        mask={cpfMask}
                        // icon={<SearchIcon />}
                        // iconPosition="insideLeft"
                        // iconColor="neutral600"
                        error={formik.touched.cpf && formik.errors.cpf}
                        // required
                        // clearable
                        // onClearClick={handleClearPatient}
                        height="40px"
                        disabled={store.surgicalOrder.orderWasLoadedInContext}
                      />
                    </S.StyledGridItem>
                  </Grid>
                  <SupportTextNew size="xxsmall" style={{ lineHeight: 1.3 }}>
                    Pacientes com até 30 dias de nascimento são considerados
                    recém-nascidos. Sendo necessário o preenchimento dos dados
                    da mãe.
                  </SupportTextNew>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                  >
                    <HeadingNew as="h3" color="textSecondary" size="medium">
                      Dados da mãe
                    </HeadingNew>
                    {!store.surgicalOrder.orderWasLoadedInContext &&
                      store.patientSearched &&
                      store.tutorSearched && (
                        <S.ClearButton size="small" onClick={handleClearTutor}>
                          Limpar
                        </S.ClearButton>
                      )}
                  </div>

                  <div style={{ marginTop: '16px' }}>
                    <Grid container spacing={2}>
                      <S.StyledGridItem item xs={12} md={6}>
                        <S.RNInputWrapper>
                          <TextFieldNew placeholder="RN" value="RN" readOnly />
                          <TextFieldNew
                            labelOverlapping
                            labelSize="xxsmall"
                            labelWeight="semiBold"
                            labelColor="textSecondary"
                            requiredColor="textSecondary"
                            label="CPF (Mãe)"
                            name="tutor.cpf"
                            placeholder="Digite CPF da mãe"
                            value={formik.values.tutor?.cpf}
                            onInputChange={handleCpfTutorChange}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            mask={cpfMask}
                            icon={<SearchIcon />}
                            iconPosition="insideLeft"
                            iconColor="neutral600"
                            error={
                              formik.touched.tutor?.cpf &&
                              formik.errors.tutor?.cpf
                            }
                            required
                            clearable
                            onClearClick={handleClearTutor}
                            height="40px"
                            disabled={
                              store.surgicalOrder.orderWasLoadedInContext
                            }
                          />
                        </S.RNInputWrapper>
                        {store.tutorSearched && !store.tutorFound && (
                          <SupportTextNew
                            size="xxsmall"
                            style={{ lineHeight: 1.3, marginTop: '8px' }}
                          >
                            CPF da mãe não encontrado
                          </SupportTextNew>
                        )}
                      </S.StyledGridItem>
                      <S.StyledGridItem item xs={12} md={6}>
                        <TextFieldNew
                          labelOverlapping
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          labelColor="textSecondary"
                          requiredColor="textSecondary"
                          label="Nome (Mãe)"
                          name="tutor.name"
                          placeholder="Informe o CPF da mãe"
                          value={formik.values.tutor?.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.tutor?.name &&
                            formik.errors.tutor?.name
                          }
                          disabled
                          readOnly
                          required
                          height="40px"
                        />
                      </S.StyledGridItem>
                      <S.StyledGridItem item xs={12} md={6}>
                        <TextFieldNew
                          labelOverlapping
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          labelColor="textSecondary"
                          requiredColor="textSecondary"
                          label="Celular (Mãe)"
                          name="tutor.phone"
                          placeholder="Digite telefone"
                          value={formik.values.tutor?.phone}
                          // onInputChange={(value) =>
                          //   formik.handleChange('tutor.phone')(value)
                          // }
                          // onBlur={formik.handleBlur('phone')}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          mask={phoneMaskWithParenthesis}
                          error={
                            formik.touched.tutor?.phone &&
                            formik.errors.tutor?.phone
                          }
                          required
                          height="40px"
                        />
                      </S.StyledGridItem>
                      <S.StyledGridItem item xs={12} md={6}>
                        <TextFieldNew
                          labelOverlapping
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          labelColor="textSecondary"
                          requiredColor="textSecondary"
                          label="E-mail (Mãe)"
                          name="tutor.email"
                          type="email"
                          placeholder="Digite e-mail"
                          value={formik.values.tutor?.email}
                          // onInputChange={(value) =>
                          //   formik.handleChange('email')(value)
                          // }
                          // onBlur={formik.handleBlur('email')}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.tutor?.email &&
                            formik.errors.tutor?.email
                          }
                          required
                          height="40px"
                        />
                      </S.StyledGridItem>
                    </Grid>
                  </div>
                </div>
              </>
            )}

          {/* Dados do paciente comum */}
          {!isPatientRN(new Date(formik.values.birthDate)) && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={2}>
                {searchBy === 'cpf' || store.patientSearched ? (
                  <S.StyledGridItem item xs={12} md={4}>
                    <TextFieldNew
                      labelOverlapping
                      labelSize="xxsmall"
                      labelWeight="semiBold"
                      labelColor="textSecondary"
                      requiredColor="textSecondary"
                      label="CPF"
                      name="cpf"
                      placeholder="Digite CPF"
                      value={formik.values.cpf}
                      onInputChange={handleCpfChange}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      mask={cpfMask}
                      icon={<SearchIcon />}
                      iconPosition="insideLeft"
                      iconColor="neutral600"
                      error={formik.touched.cpf && formik.errors.cpf}
                      required
                      clearable
                      onClearClick={handleClearPatient}
                      height="40px"
                      disabled={!!store.surgicalOrder.surgicalOrderId}
                    />
                    {store.patientSearched && !store.patientFound && (
                      <SupportTextNew
                        size="xxsmall"
                        style={{ lineHeight: 1.3, marginTop: '8px' }}
                      >
                        CPF sem cadastro, preencha os campos para cadastrar
                        paciente
                      </SupportTextNew>
                    )}
                    {store.patientSearched &&
                      store.patientFound &&
                      !store.surgicalOrder.orderWasLoadedInContext && (
                        <SupportTextNew
                          size="xxsmall"
                          style={{ lineHeight: 1.3, marginTop: '8px' }}
                        >
                          Paciente encontrado com sucesso.
                        </SupportTextNew>
                      )}
                  </S.StyledGridItem>
                ) : (
                  <>
                    <S.StyledGridItem item xs={12} md={6}>
                      <AutoCompleteNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        name="searchName"
                        onInputChange={(value) => setSearchName(value)}
                        returnFullSuggestion
                        label="Nome completo"
                        placeholder="Digite nome"
                        iconPosition="insideLeft"
                        icon={<SearchIcon />}
                        minCharactersSearch={3}
                        suggestions={searchedPatientNameList || []}
                        onSuggestionClick={(patient) =>
                          selectSearchedPatientName(patient)
                        }
                        value={searchName}
                        onType={getPatients}
                        debounceDelay={800}
                        clear={clearSelection}
                        height="40px"
                        useClearableInput
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <DatePickerNew
                        labelOverlapping
                        label="Data de nascimento"
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        placeholder={
                          searchName ? 'dd/mm/aaaa' : 'Digite nome do paciente'
                        }
                        name="searchBirthday"
                        mainValue={searchBirthday}
                        onMainChange={(date) => setSearchBirthday(date)}
                        maxDate={new Date()}
                        required
                        requiredColor="textSecondary"
                        closeOnChange
                        height="40px"
                        disabled={!searchName}
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={2}>
                      <ButtonNew
                        type="button"
                        fullWidth
                        disabled={!searchName || !searchBirthday}
                        onClick={handleSearchByNameAndBirthday}
                      >
                        Buscar
                      </ButtonNew>
                    </S.StyledGridItem>
                  </>
                )}

                {(store.patientSearched || store.isPatientComplete) && (
                  <>
                    <S.StyledGridItem item xs={12} md={8}>
                      <TextFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Nome"
                        name="name"
                        placeholder="Digite nome"
                        value={formik.values.name}
                        onInputChange={(value) =>
                          formik.handleChange('name')(value)
                        }
                        onBlur={formik.handleBlur('name')}
                        error={formik.touched.name && formik.errors.name}
                        disabled={
                          !!store.surgicalOrder.surgicalOrderId ||
                          store.patientFound
                        }
                        required
                        height="40px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <DatePickerNew
                        labelOverlapping
                        label="Data de nascimento"
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        placeholder="dd/mm/aaaa"
                        name="birthDate"
                        mainValue={formik.values.birthDate}
                        onMainChange={(date) =>
                          formik.setFieldValue('birthDate', date)
                        }
                        maxDate={new Date()}
                        disabled={
                          !!store.surgicalOrder.surgicalOrderId ||
                          store.patientFound
                        }
                        required
                        requiredColor="textSecondary"
                        closeOnChange
                        height="40px"
                        error={
                          formik.touched.birthDate && formik.errors.birthDate
                        }
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <SelectFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Sexo"
                        name="gender"
                        placeholder="Selecione sexo"
                        value={formik.values.gender}
                        onInputChange={(value) =>
                          formik.setFieldValue('gender', value)
                        }
                        error={formik.touched.gender && formik.errors.gender}
                        disabled={
                          !!store.surgicalOrder.surgicalOrderId ||
                          store.patientFound
                        }
                        required
                        items={genderNew}
                        height="42px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <SelectFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Estado civil"
                        name="maritalStatus"
                        placeholder="Digite estado civil"
                        value={formik.values.maritalStatus}
                        onInputChange={(value) =>
                          formik.setFieldValue('maritalStatus', value)
                        }
                        error={
                          formik.touched.maritalStatus &&
                          formik.errors.maritalStatus
                        }
                        disabled={
                          !!store.surgicalOrder.surgicalOrderId ||
                          store.patientFound
                        }
                        required
                        items={maritalStatus}
                        height="42px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={4}>
                      <TextFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Celular"
                        name="phone"
                        placeholder="Digite telefone"
                        value={formik.values.phone}
                        onInputChange={(value) =>
                          formik.handleChange('phone')(value)
                        }
                        onBlur={formik.handleBlur('phone')}
                        mask={phoneMaskWithParenthesis}
                        error={formik.touched.phone && formik.errors.phone}
                        required
                        height="40px"
                      />
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} md={8}>
                      <TextFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="E-mail"
                        name="email"
                        type="email"
                        placeholder="Digite e-mail"
                        value={formik.values.email}
                        onInputChange={(value) =>
                          formik.handleChange('email')(value)
                        }
                        onBlur={formik.handleBlur('email')}
                        error={formik.touched.email && formik.errors.email}
                        required
                        height="40px"
                      />
                    </S.StyledGridItem>
                  </>
                )}
              </Grid>
            </div>
          )}

          {/* Campos convênio */}
          {(store.patientSearched || store.isPatientComplete) && (
            <Grid container spacing={2}>
              <S.StyledGridItem item xs={12} md={4}>
                <SelectFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  requiredColor="textSecondary"
                  label="Convênio"
                  name="healthInsuranceCode"
                  placeholder="Selecione convênio"
                  value={formik.values.healthInsuranceCode || ''}
                  onInputChange={handleChangeHealthInsurance}
                  error={
                    formik.touched.healthInsuranceCode &&
                    formik.errors.healthInsuranceCode
                  }
                  required
                  items={insuranceOptions}
                  height="42px"
                />
              </S.StyledGridItem>
              <S.StyledGridItem item xs={12} md={4}>
                <SelectFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  label="Plano"
                  name="healthPlanCode"
                  placeholder="Selecione plano"
                  value={formik.values.healthPlanCode || ''}
                  onInputChange={handleChangeHealthPlan}
                  disabled={!formik.values.healthInsuranceCode}
                  items={healthPlanOptions}
                  height="42px"
                />
              </S.StyledGridItem>
              <S.StyledGridItem item xs={12} md={4}>
                <SelectFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  label="Subplano"
                  name="healthSubplanCode"
                  placeholder="Selecione subplano"
                  value={formik.values.healthSubplanCode || ''}
                  onInputChange={handleChangeHealthSubplan}
                  disabled={!formik.values.healthPlanCode}
                  items={healthSubPlanOptions}
                  height="42px"
                />
              </S.StyledGridItem>
              <S.StyledGridItem item xs={12} md={6}>
                <TextFieldNew
                  labelOverlapping
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  requiredColor="textSecondary"
                  label="Número carteirinha"
                  name="healthCard"
                  placeholder="Digite número da carteirinha"
                  value={formik.values.healthCard}
                  regex={numbersOnlyRegex}
                  inputMode="numeric"
                  onInputChange={(value) =>
                    formik.handleChange('healthCard')(value)
                  }
                  onBlur={formik.handleBlur('healthCard')}
                  error={formik.touched.healthCard && formik.errors.healthCard}
                  required
                  height="40px"
                />
              </S.StyledGridItem>
              <S.StyledGridItem item xs={12} md={6}>
                <DatePickerNew
                  labelOverlapping
                  label="Validade"
                  labelSize="xxsmall"
                  labelWeight="semiBold"
                  labelColor="textSecondary"
                  placeholder="dd/mm/aaaa"
                  name="validThru"
                  mainValue={formik.values.validThru}
                  onMainChange={(date) =>
                    formik.setFieldValue('validThru', date)
                  }
                  yearRange={{
                    start: moment().toDate().getFullYear(),
                    end: moment().add(20, 'years').toDate().getFullYear()
                  }}
                  minDate={new Date()}
                  closeOnChange
                  height="40px"
                  error={formik.touched.validThru && formik.errors.validThru}
                />
              </S.StyledGridItem>
            </Grid>
          )}
        </S.Section>

        {/* Cooperativa */}
        {COOPERATIVE_HOSPITALS.includes(Number(formik.values.hospitalId)) && (
          <S.Section>
            <HeadingNew as="h3" color="textSecondary" size="medium">
              Dados da cooperativa
            </HeadingNew>
            <SupportTextNew color="textPrimary" fontFamily="secondary">
              Escreva o nome da Cooperativa de cobrança do honorário médico.
            </SupportTextNew>

            <TextFieldNew
              labelOverlapping
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelColor="textSecondary"
              requiredColor="textSecondary"
              label="Cooperativa"
              name="cooperative"
              placeholder="Digite cooperativa"
              value={formik.values.cooperative}
              onInputChange={(value) =>
                formik.handleChange('cooperative')(value)
              }
              onBlur={formik.handleBlur('cooperative')}
              error={formik.touched.cooperative && formik.errors.cooperative}
              required
              height="40px"
              style={{ marginTop: '1rem' }}
            />
          </S.Section>
        )}

        <SelectPatientResponsiveModal
          isOpen={isSelectPatientModalOpen}
          onClose={handleResetSearch}
          onConfirm={handleConfirmPatient}
          patient={selectedPatient}
        />

        <PatientConfirmationResponsiveModal
          isOpen={isPatientConfirmationModalOpen}
          onClose={handleResetSearch}
          onConfirm={handleCheckCPF}
        />
      </S.FormWrapper>
    )
  })
)

export default FormRegistrationInfo
